import AdminLayout from "@/components/layout-admin";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  FileSignature,
  Plus,
  Trash2,
  Edit,
  Send,
  Eye,
  Copy,
  CheckCircle,
  Clock,
  PenLine,
  ExternalLink,
  ArrowUp,
  ArrowDown,
  Type,
  Calendar,
  User,
  Users,
  PenTool,
  Info,
  ClipboardList,
  Download,
  Shield,
  FilePlus,
  Mail,
  FileCheck,
  FileDown,
} from "lucide-react";
import type { Contract, ContractSession, ContractAuditEvent } from "@shared/schema";
import { useAdminUsers } from "@/hooks/use-resources";

type EnrichedSession = ContractSession & { contractTitle: string };

const SYSTEM_VARIABLES = [
  { key: "recipient_name", description: "Recipient's name (from send dialog)" },
  { key: "recipient_email", description: "Recipient's email address" },
  { key: "org_name", description: "Your organization name" },
  { key: "today_date", description: "Current date when document is viewed" },
  { key: "current_year", description: "Current year (e.g. 2026)" },
];

type ContractField = {
  id: string;
  label: string;
  type: "text" | "name" | "date" | "signature";
  required: boolean;
  placeholder?: string;
};

const FIELD_TYPE_ICONS: Record<ContractField["type"], typeof Type> = {
  text: Type,
  name: User,
  date: Calendar,
  signature: PenTool,
};

const FIELD_TYPE_LABELS: Record<ContractField["type"], string> = {
  text: "Text",
  name: "Name",
  date: "Date",
  signature: "Signature Pad",
};

function FieldEditor({
  fields,
  onChange,
}: {
  fields: ContractField[];
  onChange: (fields: ContractField[]) => void;
}) {
  const addField = (type: ContractField["type"] = "text") => {
    const id = `field_${Date.now()}`;
    const label = type === "signature" ? "Signature" : type === "name" ? "Full Name" : type === "date" ? "Date" : "New Field";
    onChange([
      ...fields,
      { id, label, type, required: true, placeholder: "" },
    ]);
  };

  const updateField = (idx: number, updates: Partial<ContractField>) => {
    const next = [...fields];
    next[idx] = { ...next[idx], ...updates };
    onChange(next);
  };

  const removeField = (idx: number) => {
    onChange(fields.filter((_, i) => i !== idx));
  };

  const moveField = (idx: number, direction: "up" | "down") => {
    const next = [...fields];
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= next.length) return;
    [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {fields.map((field, idx) => {
        const FieldIcon = FIELD_TYPE_ICONS[field.type];
        return (
          <div key={field.id} className="border rounded-lg p-3 bg-muted/30 space-y-2" data-testid={`field-editor-${idx}`}>
            <div className="flex gap-2 items-start">
              <div className="flex flex-col gap-0.5 pt-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  disabled={idx === 0}
                  onClick={() => moveField(idx, "up")}
                  data-testid={`field-move-up-${idx}`}
                >
                  <ArrowUp className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  disabled={idx === fields.length - 1}
                  onClick={() => moveField(idx, "down")}
                  data-testid={`field-move-down-${idx}`}
                >
                  <ArrowDown className="h-3 w-3" />
                </Button>
              </div>
              <div className="flex-1 space-y-2">
                <Input
                  placeholder="Field label"
                  value={field.label}
                  onChange={(e) => updateField(idx, { label: e.target.value })}
                  data-testid={`field-label-${idx}`}
                />
                <div className="flex gap-2 flex-wrap">
                  <Select
                    value={field.type}
                    onValueChange={(v) => updateField(idx, { type: v as ContractField["type"] })}
                  >
                    <SelectTrigger className="w-40" data-testid={`field-type-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">
                        <span className="flex items-center gap-1.5"><Type className="h-3 w-3" /> Text</span>
                      </SelectItem>
                      <SelectItem value="name">
                        <span className="flex items-center gap-1.5"><User className="h-3 w-3" /> Name</span>
                      </SelectItem>
                      <SelectItem value="date">
                        <span className="flex items-center gap-1.5"><Calendar className="h-3 w-3" /> Date</span>
                      </SelectItem>
                      <SelectItem value="signature">
                        <span className="flex items-center gap-1.5"><PenTool className="h-3 w-3" /> Signature Pad</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {field.type !== "signature" && (
                    <Input
                      placeholder="Placeholder text"
                      value={field.placeholder || ""}
                      onChange={(e) => updateField(idx, { placeholder: e.target.value })}
                      className="flex-1"
                      data-testid={`field-placeholder-${idx}`}
                    />
                  )}
                  <div className="flex items-center gap-1.5">
                    <Switch
                      checked={field.required}
                      onCheckedChange={(v) => updateField(idx, { required: v })}
                      data-testid={`field-required-${idx}`}
                    />
                    <span className="text-xs text-muted-foreground">Required</span>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:text-destructive"
                onClick={() => removeField(idx)}
                data-testid={`field-remove-${idx}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                <FieldIcon className="h-3 w-3 mr-1" />
                {FIELD_TYPE_LABELS[field.type]}
              </Badge>
              <p className="text-xs text-muted-foreground">
                Variable: <code className="bg-muted px-1 rounded">{`{{${field.id}}}`}</code>
              </p>
            </div>
          </div>
        );
      })}
      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => addField("text")} data-testid="button-add-field">
          <Type className="h-3.5 w-3.5 mr-1" /> Add Text
        </Button>
        <Button variant="outline" size="sm" onClick={() => addField("name")} data-testid="button-add-name-field">
          <User className="h-3.5 w-3.5 mr-1" /> Add Name
        </Button>
        <Button variant="outline" size="sm" onClick={() => addField("date")} data-testid="button-add-date-field">
          <Calendar className="h-3.5 w-3.5 mr-1" /> Add Date
        </Button>
        <Button variant="outline" size="sm" onClick={() => addField("signature")} data-testid="button-add-signature-field">
          <PenTool className="h-3.5 w-3.5 mr-1" /> Add Signature
        </Button>
      </div>
    </div>
  );
}

function ContractFormDialog({
  open,
  onClose,
  contract,
}: {
  open: boolean;
  onClose: () => void;
  contract?: Contract;
}) {
  const { toast } = useToast();
  const isEdit = !!contract;

  const [title, setTitle] = useState(contract?.title || "");
  const [body, setBody] = useState(contract?.body || "");
  const [fields, setFields] = useState<ContractField[]>(
    (contract?.fields as ContractField[]) || []
  );
  const [isActive, setIsActive] = useState(contract?.isActive ?? true);
  const [showLogo, setShowLogo] = useState((contract as any)?.showLogo ?? false);

  const createMutation = useMutation({
    mutationFn: (data: object) =>
      apiRequest("POST", "/api/admin/contracts", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contracts"] });
      toast({ title: "Contract created" });
      onClose();
    },
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: (data: object) =>
      apiRequest("PATCH", `/api/admin/contracts/${contract?.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contracts"] });
      toast({ title: "Contract updated" });
      onClose();
    },
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  const handleSave = () => {
    if (!title.trim()) {
      toast({ title: "Title is required", variant: "destructive" });
      return;
    }
    if (!body.trim()) {
      toast({ title: "Contract body is required", variant: "destructive" });
      return;
    }
    const data = { title, body, fields, isActive, showLogo };
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Contract" : "New Contract Template"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Short-Term Rental Agreement"
              data-testid="input-contract-title"
            />
          </div>

          <div>
            <Label className="mb-1 block">
              Contract Body
              <span className="text-xs font-normal text-muted-foreground ml-2">
                Use {"{{field_id}}"} to insert field values. Supports basic formatting.
              </span>
            </Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Enter the full contract text here. You can use line breaks for paragraphs and {{field_id}} for variable substitution..."
              rows={14}
              className="font-mono text-sm"
              data-testid="textarea-contract-body"
            />
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-1.5">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <p className="text-xs font-medium text-blue-800 dark:text-blue-300">System Variables</p>
            </div>
            <p className="text-xs text-blue-700 dark:text-blue-400">
              These are auto-filled when the recipient opens the signing page. Click to copy.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {SYSTEM_VARIABLES.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  className="inline-flex items-center gap-1 bg-white dark:bg-blue-900/50 border border-blue-200 dark:border-blue-700 rounded px-2 py-0.5 text-xs font-mono text-blue-800 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors cursor-pointer"
                  title={v.description}
                  onClick={() => {
                    navigator.clipboard.writeText(`{{${v.key}}}`);
                  }}
                  data-testid={`sysvar-${v.key}`}
                >
                  {`{{${v.key}}}`}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Custom Fields (filled by signer)</Label>
            <p className="text-xs text-muted-foreground mb-3">
              Add fields in the order they should appear to the signer. Use the arrows to reorder.
            </p>
            <FieldEditor fields={fields} onChange={setFields} />
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={isActive}
              onCheckedChange={setIsActive}
              data-testid="switch-contract-active"
            />
            <Label>Active (can be sent to guests)</Label>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={showLogo}
              onCheckedChange={setShowLogo}
              data-testid="switch-contract-show-logo"
            />
            <Label>Show organization logo on signing page</Label>
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isPending} data-testid="button-save-contract">
              {isPending ? "Saving..." : isEdit ? "Save Changes" : "Create Contract"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SendContractDialog({
  open,
  onClose,
  contract,
}: {
  open: boolean;
  onClose: () => void;
  contract: Contract;
}) {
  const { toast } = useToast();
  const { data: users = [] } = useAdminUsers();
  const [multiSigner, setMultiSigner] = useState(false);
  const [recipientMode, setRecipientMode] = useState<"org_user" | "manual">("manual");
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [signers, setSigners] = useState([{ name: "", email: "" }, { name: "", email: "" }]);
  const [sent, setSent] = useState(false);
  const [sentResult, setSentResult] = useState<{ signingUrl?: string; isMultiSigner?: boolean; signers?: any[] } | null>(null);

  const orgUsers = (users as any[]).filter(u => u.email || u.fullName);

  const sendMutation = useMutation({
    mutationFn: (data: object) =>
      apiRequest("POST", `/api/admin/contracts/${contract.id}/send`, data).then(r => r.json()),
    onSuccess: (res: any) => {
      setSentResult(res);
      setSent(true);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contracts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contract-sessions"] });
    },
    onError: () => toast({ title: "Failed to send", variant: "destructive" }),
  });

  const handleSend = () => {
    if (multiSigner) {
      const validSigners = signers.filter(s => s.email.trim());
      if (validSigners.length < 2) {
        toast({ title: "Add at least 2 signers with email addresses", variant: "destructive" });
        return;
      }
      sendMutation.mutate({ signers: validSigners });
      return;
    }
    if (recipientMode === "org_user" && selectedUserId) {
      const user = (users as any[]).find(u => u.id === Number(selectedUserId));
      sendMutation.mutate({ userId: Number(selectedUserId), guestName: user?.fullName || user?.username || undefined, guestEmail: user?.email || undefined });
    } else {
      sendMutation.mutate({ guestName: guestName || undefined, guestEmail: guestEmail || undefined });
    }
  };

  const handleClose = () => {
    setSent(false);
    setSentResult(null);
    setGuestName("");
    setGuestEmail("");
    setSelectedUserId("");
    setRecipientMode("manual");
    setMultiSigner(false);
    setSigners([{ name: "", email: "" }, { name: "", email: "" }]);
    onClose();
  };

  const updateSigner = (idx: number, field: "name" | "email", val: string) => {
    setSigners(prev => prev.map((s, i) => i === idx ? { ...s, [field]: val } : s));
  };

  const addSigner = () => setSigners(prev => [...prev, { name: "", email: "" }]);
  const removeSigner = (idx: number) => setSigners(prev => prev.filter((_, i) => i !== idx));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send Contract for Signing</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground font-medium">{contract.title}</p>

          {!sent ? (
            <>
              {/* Multi-signer toggle */}
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Sequential Multi-Signer</p>
                    <p className="text-xs text-muted-foreground">Send to multiple people in order</p>
                  </div>
                </div>
                <Switch checked={multiSigner} onCheckedChange={setMultiSigner} data-testid="switch-multi-signer" />
              </div>

              {multiSigner ? (
                /* Multi-signer form */
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Signers (in order)</Label>
                  {signers.map((s, idx) => (
                    <div key={idx} className="flex gap-2 items-start">
                      <div className="flex-none w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold mt-2">
                        {idx + 1}
                      </div>
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Name"
                          value={s.name}
                          onChange={e => updateSigner(idx, "name", e.target.value)}
                          data-testid={`input-signer-name-${idx}`}
                        />
                        <Input
                          type="email"
                          placeholder="Email *"
                          value={s.email}
                          onChange={e => updateSigner(idx, "email", e.target.value)}
                          data-testid={`input-signer-email-${idx}`}
                        />
                      </div>
                      {signers.length > 2 && (
                        <Button size="icon" variant="ghost" className="mt-1 h-8 w-8" onClick={() => removeSigner(idx)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addSigner} data-testid="button-add-signer">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Signer
                  </Button>
                  <p className="text-xs text-muted-foreground">Each signer receives their link by email only after the previous signer completes.</p>
                </div>
              ) : (
                /* Single-signer form */
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Recipient</Label>
                  <div className="flex gap-2">
                    <Button variant={recipientMode === "org_user" ? "default" : "outline"} size="sm" onClick={() => setRecipientMode("org_user")} data-testid="button-recipient-org">
                      <User className="h-3.5 w-3.5 mr-1" /> Select User
                    </Button>
                    <Button variant={recipientMode === "manual" ? "default" : "outline"} size="sm" onClick={() => setRecipientMode("manual")} data-testid="button-recipient-manual">
                      <Type className="h-3.5 w-3.5 mr-1" /> Enter Manually
                    </Button>
                  </div>
                  {recipientMode === "org_user" ? (
                    <div>
                      <Label>Select User from Organization</Label>
                      <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                        <SelectTrigger data-testid="select-org-user"><SelectValue placeholder="Choose a user..." /></SelectTrigger>
                        <SelectContent>
                          {orgUsers.map((u: any) => (
                            <SelectItem key={u.id} value={String(u.id)}>
                              {u.fullName || u.username} {u.email ? `(${u.email})` : ""} — {u.role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <>
                      <div>
                        <Label>Recipient Name (optional)</Label>
                        <Input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="e.g. John Smith" data-testid="input-guest-name" />
                      </div>
                      <div>
                        <Label>Recipient Email (optional — sends link by email)</Label>
                        <Input type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="guest@example.com" data-testid="input-guest-email" />
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSend} disabled={sendMutation.isPending} data-testid="button-generate-link">
                  <Send className="h-4 w-4 mr-1" />
                  {sendMutation.isPending ? "Sending..." : multiSigner ? "Send to Signers" : "Generate Signing Link"}
                </Button>
              </div>
            </>
          ) : (
            /* Success state */
            <>
              {sentResult?.isMultiSigner ? (
                <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 text-green-800 dark:text-green-300">
                    <CheckCircle className="h-5 w-5" />
                    <p className="text-sm font-medium">Signing sequence started!</p>
                  </div>
                  <div className="space-y-2">
                    {(sentResult.signers || []).map((s: any, idx: number) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <div className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">{idx + 1}</div>
                        <span className="font-medium">{s.name || s.email}</span>
                        <span className="text-muted-foreground text-xs">{s.email}</span>
                        {idx === 0 ? <Badge variant="outline" className="text-xs ml-auto">Email sent</Badge> : <Badge variant="secondary" className="text-xs ml-auto">Waiting</Badge>}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">Each signer will receive their email automatically after the previous signer completes.</p>
                </div>
              ) : (
                <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-3">
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">Signing link generated!</p>
                  <div className="flex gap-2">
                    <Input readOnly value={`${window.location.origin}${sentResult?.signingUrl || ""}`} className="font-mono text-xs" data-testid="text-signing-url" />
                    <Button size="icon" variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}${sentResult?.signingUrl || ""}`); toast({ title: "Copied!" }); }} data-testid="button-copy-link">
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="outline" onClick={() => window.open(`${window.location.origin}${sentResult?.signingUrl}`, "_blank")} data-testid="button-open-link">
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                  {(guestEmail || selectedUserId) && <p className="text-xs text-muted-foreground">An email was sent to the recipient</p>}
                </div>
              )}
              <div className="flex justify-end">
                <Button onClick={handleClose} data-testid="button-done">Done</Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SessionsDrawer({
  open,
  onClose,
  contract,
}: {
  open: boolean;
  onClose: () => void;
  contract: Contract | null;
}) {
  const { data: sessions = [] } = useQuery<ContractSession[]>({
    queryKey: ["/api/admin/contracts", contract?.id, "sessions"],
    queryFn: () =>
      fetch(`/api/admin/contracts/${contract?.id}/sessions`).then((r) => r.json()),
    enabled: !!contract,
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Signing Sessions — {contract?.title}</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto space-y-2 max-h-[60vh]">
          {sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No signing sessions yet. Use "Send" to generate a link.
            </p>
          ) : (
            sessions.map((s) => (
              <div
                key={s.id}
                className="border rounded-lg p-3 space-y-2"
                data-testid={`session-row-${s.id}`}
              >
                {/* Multi-signer progress */}
                {s.isMultiSigner && Array.isArray(s.signers) && s.signers.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <Users className="h-3.5 w-3.5" />
                      Multi-signer — {(s.signers as any[]).filter((sg: any) => sg.status === "signed").length}/{s.signers.length} signed
                    </div>
                    {(s.signers as any[]).map((sg: any, idx: number) => (
                      <div key={idx} className="flex items-center gap-2 text-xs pl-4">
                        <div className="w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold bg-muted">{idx + 1}</div>
                        <span className="font-medium truncate max-w-[120px]">{sg.name || sg.email}</span>
                        {sg.status === "signed" ? (
                          <Badge variant="outline" className="text-green-700 border-green-300 text-[10px] py-0 h-4">
                            <CheckCircle className="h-2.5 w-2.5 mr-0.5" /> Signed
                          </Badge>
                        ) : sg.status === "pending" ? (
                          <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px] py-0 h-4">
                            <Clock className="h-2.5 w-2.5 mr-0.5" /> Their turn
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px] py-0 h-4">Waiting</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    {!s.isMultiSigner && <p className="font-medium text-sm">{s.guestName || "—"}</p>}
                    {!s.isMultiSigner && <p className="text-xs text-muted-foreground">{s.guestEmail || "No email"}</p>}
                    {s.signedAt && (
                      <p className="text-xs text-muted-foreground">
                        Signed {new Date(s.signedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {s.status === "signed" ? (
                      <Badge variant="outline" className="text-green-700 border-green-300">
                        <CheckCircle className="h-3 w-3 mr-1" /> Signed
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-700 border-amber-300">
                        <Clock className="h-3 w-3 mr-1" /> {s.isMultiSigner ? "In Progress" : "Pending"}
                      </Badge>
                    )}
                    {s.status === "pending" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const url = `${window.location.origin}/sign/${s.token}`;
                          navigator.clipboard.writeText(url);
                        }}
                        data-testid={`button-copy-session-${s.id}`}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                {/* Multi-signer: per-signer signatures */}
                {s.isMultiSigner && Array.isArray(s.signers) && s.signers.some((sg: any) => sg.status === "signed") && (
                  <div className="space-y-2">
                    {(s.signers as any[]).filter((sg: any) => sg.status === "signed").map((sg: any, idx: number) => (
                      <div key={idx} className="bg-muted/50 rounded p-2 space-y-1.5">
                        <p className="text-xs font-medium">{sg.name || sg.email} (Signer {(s.signers as any[]).indexOf(sg) + 1})</p>
                        {sg.fieldValues && Object.keys(sg.fieldValues).length > 0 && (
                          <div className="space-y-0.5">
                            {Object.entries(sg.fieldValues as Record<string, string>).filter(([, val]) => typeof val !== 'string' || !val.startsWith('data:image')).map(([key, val]) => (
                              <p key={key} className="text-xs">
                                <span className="text-muted-foreground">{key}:</span> {String(val)}
                              </p>
                            ))}
                          </div>
                        )}
                        {sg.signatureImageData && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Signature:</span>
                            <img src={sg.signatureImageData} alt="Signature" className="h-10 border rounded" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* Single-signer: field values + signature */}
                {!s.isMultiSigner && s.status === "signed" && s.fieldValues && (
                  <div className="bg-muted/50 rounded p-2 space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Field Values:</p>
                    {Object.entries(s.fieldValues as Record<string, string>).map(([key, val]) => (
                      <p key={key} className="text-xs">
                        <span className="text-muted-foreground">{key}:</span> {typeof val === 'string' && val.startsWith('data:image') ? '(signature)' : String(val)}
                      </p>
                    ))}
                  </div>
                )}
                {!s.isMultiSigner && s.signatureImageData && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Signature:</span>
                    <img
                      src={s.signatureImageData}
                      alt="Signature"
                      className="h-10 border rounded"
                    />
                  </div>
                )}
                <div className="bg-muted/30 rounded-lg p-3 space-y-2" data-testid={`drawer-audit-section-${s.id}`}>
                  <div className="flex items-center gap-1.5">
                    <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                    <p className="text-xs font-medium">Audit Trail</p>
                  </div>
                  <AuditTimeline sessionId={s.id} />
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const AUDIT_EVENT_CONFIG: Record<string, { label: string; icon: typeof Shield; color: string }> = {
  created: { label: "Session Created", icon: FilePlus, color: "text-blue-600" },
  link_sent: { label: "Signing Link Sent", icon: Mail, color: "text-indigo-600" },
  viewed: { label: "Document Viewed", icon: Eye, color: "text-amber-600" },
  signed: { label: "Document Signed", icon: FileCheck, color: "text-green-600" },
  pdf_downloaded: { label: "PDF Downloaded", icon: FileDown, color: "text-purple-600" },
};

function getAuditEventConfig(eventKey: string): { label: string; icon: typeof Shield; color: string } {
  if (AUDIT_EVENT_CONFIG[eventKey]) return AUDIT_EVENT_CONFIG[eventKey];
  const signerMatch = eventKey.match(/^signed_by_(?:signer_)?(\d+)$/);
  if (signerMatch) {
    return { label: `Signed by Signer ${signerMatch[1]}`, icon: FileCheck, color: "text-green-600" };
  }
  return { label: eventKey, icon: Shield, color: "text-muted-foreground" };
}

function AuditTimeline({ sessionId }: { sessionId: number }) {
  const { data: events = [], isLoading } = useQuery<ContractAuditEvent[]>({
    queryKey: ["/api/admin/contract-sessions", sessionId, "audit"],
    queryFn: async () => {
      const r = await fetch(`/api/admin/contract-sessions/${sessionId}/audit`);
      if (!r.ok) return [];
      return r.json();
    },
  });

  if (isLoading) {
    return <div className="h-16 bg-muted animate-pulse rounded" data-testid="audit-loading" />;
  }

  if (events.length === 0) {
    return (
      <div className="text-xs text-muted-foreground py-2 text-center" data-testid="audit-empty">
        No audit events recorded for this session.
      </div>
    );
  }

  return (
    <div className="space-y-0" data-testid={`audit-timeline-${sessionId}`}>
      {events.map((event, idx) => {
        const config = getAuditEventConfig(event.event);
        const Icon = config.icon;
        return (
          <div key={event.id} className="flex gap-3 relative" data-testid={`audit-event-${event.id}`}>
            <div className="flex flex-col items-center">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center bg-muted shrink-0 ${config.color}`}>
                <Icon className="h-3 w-3" />
              </div>
              {idx < events.length - 1 && (
                <div className="w-px bg-border flex-1 min-h-[16px]" />
              )}
            </div>
            <div className="pb-3 min-w-0">
              <p className="text-xs font-medium">{config.label}</p>
              <p className="text-[10px] text-muted-foreground">
                {new Date(event.occurredAt).toLocaleString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  timeZoneName: "short",
                })}
              </p>
              {event.ipAddress && (
                <p className="text-[10px] text-muted-foreground">
                  IP: {event.ipAddress}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AllSessionsTab() {
  const { toast } = useToast();
  const { data: sessions = [], isLoading } = useQuery<EnrichedSession[]>({
    queryKey: ["/api/admin/contract-sessions"],
  });

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const filtered = statusFilter === "all" ? sessions : sessions.filter(s => s.status === statusFilter);
  const signedCount = sessions.filter(s => s.status === "signed").length;
  const pendingCount = sessions.filter(s => s.status === "pending").length;

  if (isLoading) {
    return (
      <div className="grid gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-2 text-sm">
          <Badge variant="outline" className="text-green-700 border-green-300">{signedCount} signed</Badge>
          <Badge variant="outline" className="text-amber-700 border-amber-300">{pendingCount} pending</Badge>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36" data-testid="select-session-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sessions</SelectItem>
            <SelectItem value="signed">Signed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardList className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">No signing sessions found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
          {filtered.map((s) => (
            <Card key={s.id} data-testid={`all-session-row-${s.id}`}>
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">{s.contractTitle}</p>
                      {s.status === "signed" ? (
                        <Badge variant="outline" className="text-green-700 border-green-300 text-xs shrink-0">
                          <CheckCircle className="h-3 w-3 mr-1" /> Signed
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-700 border-amber-300 text-xs shrink-0">
                          <Clock className="h-3 w-3 mr-1" /> Pending
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                      <span>{s.guestName || "No name"}</span>
                      {s.guestEmail && <span>{s.guestEmail}</span>}
                      {s.signedAt && <span>Signed {new Date(s.signedAt).toLocaleDateString()}</span>}
                      {!s.signedAt && s.createdAt && <span>Sent {new Date(s.createdAt).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {s.status === "pending" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/sign/${s.token}`);
                        }}
                        data-testid={`button-copy-all-session-${s.id}`}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                      data-testid={`button-expand-session-${s.id}`}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {expandedId === s.id && (
                  <div className="border-t pt-2 space-y-2">
                    {s.fieldValues && Object.keys(s.fieldValues as Record<string, string>).length > 0 && (
                      <div className="bg-muted/50 rounded p-2 space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Field Values:</p>
                        {Object.entries(s.fieldValues as Record<string, string>).map(([key, val]) => (
                          <p key={key} className="text-xs">
                            <span className="text-muted-foreground">{key}:</span>{" "}
                            {typeof val === "string" && val.startsWith("data:image") ? "(signature)" : String(val)}
                          </p>
                        ))}
                      </div>
                    )}
                    {s.signatureImageData && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Signature:</span>
                        <img src={s.signatureImageData} alt="Signature" className="h-12 border rounded" />
                      </div>
                    )}
                    {!s.signatureImageData && s.status === "signed" && (
                      <p className="text-xs text-muted-foreground">No signature image captured</p>
                    )}
                    <div className="bg-muted/30 rounded-lg p-3 space-y-2" data-testid={`audit-section-${s.id}`}>
                      <div className="flex items-center gap-1.5">
                        <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                        <p className="text-xs font-medium">Audit Trail</p>
                      </div>
                      <AuditTimeline sessionId={s.id} />
                    </div>
                    {s.status === "signed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const res = await apiRequest("GET", `/api/contract-sessions/${s.id}/download`);
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `contract_${s.id}_signed.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          } catch {
                            toast({ title: "Download failed", variant: "destructive" });
                          }
                        }}
                        data-testid={`button-download-session-${s.id}`}
                      >
                        <Download className="h-3.5 w-3.5 mr-1" /> Download PDF
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminContracts() {
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editContract, setEditContract] = useState<Contract | null>(null);
  const [sendContract, setSendContract] = useState<Contract | null>(null);
  const [viewSessions, setViewSessions] = useState<Contract | null>(null);
  const [activeTab, setActiveTab] = useState("templates");

  const { data: contracts = [], isLoading } = useQuery<Contract[]>({
    queryKey: ["/api/admin/contracts"],
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiRequest("DELETE", `/api/admin/contracts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contracts"] });
      toast({ title: "Contract deleted" });
    },
    onError: () => toast({ title: "Failed to delete", variant: "destructive" }),
  });

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileSignature className="h-6 w-6" />
              Contracts
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Create and send e-signature contracts
            </p>
          </div>
          {activeTab === "templates" && (
            <Button onClick={() => setShowForm(true)} data-testid="button-new-contract">
              <Plus className="h-4 w-4 mr-1" /> New Contract
            </Button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList data-testid="tabs-contracts">
            <TabsTrigger value="templates" data-testid="tab-templates">
              <FileSignature className="h-3.5 w-3.5 mr-1" /> Templates
            </TabsTrigger>
            <TabsTrigger value="sessions" data-testid="tab-sessions">
              <ClipboardList className="h-3.5 w-3.5 mr-1" /> Signed Sessions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="templates" className="mt-4">
            {isLoading ? (
              <div className="grid gap-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
                ))}
              </div>
            ) : contracts.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <PenLine className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No contracts yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Create your first contract template to send for e-signature.
                  </p>
                  <Button
                    className="mt-4"
                    onClick={() => setShowForm(true)}
                    data-testid="button-create-first"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Create Contract
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {contracts.map((contract) => (
                  <Card key={contract.id} data-testid={`card-contract-${contract.id}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold truncate">{contract.title}</h3>
                          {contract.isActive ? (
                            <Badge variant="outline" className="text-green-700 border-green-300 text-xs">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground text-xs">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {(contract.fields as ContractField[]).length} custom field
                          {(contract.fields as ContractField[]).length !== 1 ? "s" : ""} ·{" "}
                          {new Date(contract.createdAt!).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-muted-foreground truncate mt-1 max-w-xl">
                          {contract.body.slice(0, 120)}
                          {contract.body.length > 120 ? "..." : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setViewSessions(contract)}
                          data-testid={`button-view-sessions-${contract.id}`}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" /> Sessions
                        </Button>
                        {contract.isActive && (
                          <Button
                            size="sm"
                            onClick={() => setSendContract(contract)}
                            data-testid={`button-send-${contract.id}`}
                          >
                            <Send className="h-3.5 w-3.5 mr-1" /> Send
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditContract(contract)}
                          data-testid={`button-edit-${contract.id}`}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              data-testid={`button-delete-${contract.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete contract?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete "{contract.title}" and all its signing sessions.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground"
                                onClick={() => deleteMutation.mutate(contract.id)}
                                data-testid={`button-confirm-delete-${contract.id}`}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="sessions" className="mt-4">
            <AllSessionsTab />
          </TabsContent>
        </Tabs>
      </div>

      {showForm && (
        <ContractFormDialog
          open={showForm}
          onClose={() => setShowForm(false)}
        />
      )}
      {editContract && (
        <ContractFormDialog
          open={!!editContract}
          onClose={() => setEditContract(null)}
          contract={editContract}
        />
      )}
      {sendContract && (
        <SendContractDialog
          open={!!sendContract}
          onClose={() => setSendContract(null)}
          contract={sendContract}
        />
      )}
      {viewSessions && (
        <SessionsDrawer
          open={!!viewSessions}
          onClose={() => setViewSessions(null)}
          contract={viewSessions}
        />
      )}
    </AdminLayout>
  );
}
